import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FilterListIcon from '@material-ui/icons/FilterList';
import PaletteOutlinedIcon from '@material-ui/icons/PaletteOutlined';
import SearchIcon from '@material-ui/icons/Search';
import StraightenOutlinedIcon from '@material-ui/icons/StraightenOutlined';
import SwapVertIcon from '@material-ui/icons/SwapVert';
import ToggleOffOutlinedIcon from '@material-ui/icons/ToggleOffOutlined';
import ViewListOutlinedIcon from '@material-ui/icons/ViewListOutlined';
import OrderedZoneBoard from 'components/kea/OrderedZoneBoard';
import PinAction from 'components/kea/PinAction';

const interpolate = (message, values = {}) => Object.keys(values).reduce(
  (result, key) => result.replace(new RegExp(`\\{${key}\\}`, 'g'), String(values[key])),
  message,
);
const fallbackTranslate = (id, fallback, values) => interpolate(fallback, values);

const typeLabel = (type, t) => ({
  string: t('facets.policy.board.type.list', 'List'),
  boolean: t('facets.policy.board.type.boolean', 'Boolean'),
  color: t('facets.policy.board.type.color', 'Color'),
  number: t('facets.policy.board.type.range', 'Range'),
}[type] || type);

const FacetTypeIcon = ({ type }) => {
  if (type === 'boolean') return <ToggleOffOutlinedIcon />;
  if (type === 'color') return <PaletteOutlinedIcon />;
  if (type === 'number') return <StraightenOutlinedIcon />;
  return <ViewListOutlinedIcon />;
};

FacetTypeIcon.propTypes = { type: PropTypes.string.isRequired };

const FacetRow = ({ facet, zone, index, onOpen, onMove, translate: t }) => {
  const fixed = zone === 'first' || zone === 'last';
  const hidden = zone === 'hidden';
  const togglePin = () => onMove(facet.id, zone, fixed ? 'automatic' : 'first');
  const toggleHidden = () => onMove(facet.id, zone, hidden ? 'automatic' : 'hidden');
  return (
    <>
      <span
        className={`fp-facet-position ${fixed ? `is-${zone}` : ''}`}
        aria-label={fixed ? t('facets.policy.board.fixedPosition', 'Fixed position {position}', { position: index + 1 }) : undefined}
      >
        {fixed ? index + 1 : ''}
      </span>
      <span className="fp-drag-signifier" aria-hidden="true"><DragIndicatorIcon /></span>
      <span
        className={`fp-facet-type-icon fp-facet-type-icon--${facet.type}`}
        role="img"
        aria-label={t('facets.policy.board.typeLabel', '{type} filter', { type: typeLabel(facet.type, t) })}
      >
        <FacetTypeIcon type={facet.type} />
      </span>
      <span className="fp-facet-copy">
        <span className="fp-facet-title-line">
          <strong>{facet.title}</strong>
        </span>
        <span className="fp-facet-key">{facet.subtitle || facet.key}</span>
      </span>
      <span className="fp-facet-stats">
        {Number.isFinite(facet.values) && <span><strong>{facet.values.toLocaleString()}</strong> {t('facets.policy.board.values', 'values')}</span>}
        {Number.isFinite(facet.products) && <span><strong>{facet.products.toLocaleString()}</strong> {t('facets.policy.board.products', 'products')}</span>}
      </span>
      <span className="fp-facet-health">
        {facet.health === 'attention' && (
          <span className="fp-facet-warning"><ErrorOutlineIcon />{facet.issue}</span>
        )}
      </span>
      <span className="fp-row-actions">
        {!hidden && (
          <PinAction
            pinned={fixed}
            revealOnHover={false}
            className="fp-row-pin-action"
            label={fixed
              ? t('facets.policy.board.unpin', 'Unpin {title}', { title: facet.title })
              : t('facets.policy.board.pin', 'Pin {title}', { title: facet.title })}
            onClick={(event) => { event.stopPropagation(); togglePin(); }}
          />
        )}
        {hidden && <span className="fp-action-spacer" aria-hidden="true" />}
        <button
          type="button"
          className="fp-icon-button fp-row-state-action"
          aria-label={hidden
            ? t('facets.policy.board.restore', 'Restore {title}', { title: facet.title })
            : t('facets.policy.board.hide', 'Hide {title}', { title: facet.title })}
          onClick={(event) => { event.stopPropagation(); toggleHidden(); }}
        >
          {hidden ? <CheckIcon /> : <CloseIcon />}
        </button>
        <button
          type="button"
          className="fp-icon-button fp-row-edit-action"
          aria-label={t('facets.policy.board.edit', 'Edit {title}', { title: facet.title })}
          onClick={(event) => { event.stopPropagation(); onOpen(facet.id); }}
        >
          <EditOutlinedIcon />
        </button>
      </span>
    </>
  );
};

FacetRow.propTypes = {
  facet: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    values: PropTypes.number,
    products: PropTypes.number,
    health: PropTypes.string,
    issue: PropTypes.string,
  }).isRequired,
  zone: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  onOpen: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  translate: PropTypes.func,
};

FacetRow.defaultProps = { translate: fallbackTranslate };

const FacetOrderBoard = ({ facets, zones, onZonesChange, onOpen, onReset, onSave, dirty, translate: t }) => {
  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const zoneCopy = useMemo(() => ({
    first: {
      eyebrow: t('facets.policy.board.zone.fixed', 'Fixed'),
      title: t('facets.policy.board.zone.first.title', 'Fix at top'),
      description: t('facets.policy.board.zone.first.description', 'Exact positions at the start of the filter list.'),
    },
    automatic: {
      eyebrow: t('facets.policy.board.zone.automatic.eyebrow', 'Automatic'),
      title: t('facets.policy.board.zone.automatic.title', 'Automatic order'),
      description: t('facets.policy.board.zone.automatic.description', 'Kea orders these filters by usefulness.'),
    },
    last: {
      eyebrow: t('facets.policy.board.zone.fixed', 'Fixed'),
      title: t('facets.policy.board.zone.last.title', 'Fix at bottom'),
      description: t('facets.policy.board.zone.last.description', 'Exact positions at the end of the filter list.'),
    },
    hidden: {
      title: t('facets.policy.board.zone.hidden.title', 'Hidden filters'),
      persistent: true,
    },
  }), [t]);
  const typeFilterOptions = [
    ['all', t('facets.policy.board.filter.all', 'All')],
    ['string', t('facets.policy.board.filter.lists', 'Lists')],
    ['boolean', t('facets.policy.board.filter.boolean', 'Boolean')],
    ['color', t('facets.policy.board.filter.colors', 'Colors')],
    ['number', t('facets.policy.board.filter.ranges', 'Ranges')],
  ];

  const visible = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const result = {};
    Object.keys(zones).forEach((zone) => {
      result[zone] = zones[zone].filter((id) => {
        const facet = facets[id];
        if (!facet) return false;
        if (type !== 'all' && facet.type !== type) return false;
        return !normalized
          || facet.title.toLowerCase().includes(normalized)
          || facet.key.toLowerCase().includes(normalized);
      });
    });
    return result;
  }, [facets, query, type, zones]);

  const moveFacet = (facetId, from, to, requestedIndex) => {
    if (from === to) return;
    const next = Object.keys(zones).reduce((copy, key) => ({ ...copy, [key]: [...zones[key]] }), {});
    next[from] = next[from].filter(id => id !== facetId);
    const destination = next[to];
    const index = Number.isInteger(requestedIndex)
      ? Math.max(0, Math.min(requestedIndex, destination.length))
      : destination.length;
    destination.splice(index, 0, facetId);
    onZonesChange(next);
    setSaved(false);
  };

  const onBoardMove = ({ itemId, sourceZoneId: from, sourceIndex, destinationZoneId: to, destinationIndex }) => {
    if (query || type !== 'all') return;
    if (from === to) {
      if (from === 'automatic' || sourceIndex === destinationIndex) return;
      const next = { ...zones, [from]: [...zones[from]] };
      const [moved] = next[from].splice(sourceIndex, 1);
      next[from].splice(destinationIndex, 0, moved);
      onZonesChange(next);
      setSaved(false);
      return;
    }
    moveFacet(itemId, from, to, destinationIndex);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onSave();
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2400);
    } catch {
      // The production adapter owns the visible error state.
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fp-page">
      <header className="fp-page-header">
        <h1>{t('facets.policy.facets', 'Filters')}</h1>
        <div className="fp-page-actions">
          {dirty && <button type="button" className="fp-button fp-button--quiet" onClick={onReset}>{t('appModule.reset', 'Reset')}</button>}
          <button type="button" className="fp-button fp-button--primary" disabled={!dirty || saving} onClick={handleSave}>
            {saving
              ? t('facets.policy.order.saving', 'Saving order…')
              : saved
                ? t('facets.policy.editor.saved', 'Changes saved')
                : t('facets.policy.board.saveOrder', 'Save order')}
          </button>
        </div>
      </header>

      <div className="fp-toolbar">
        <label className="fp-search-field">
          <SearchIcon aria-hidden="true" />
          <span className="sr-only">{t('facets.policy.board.search', 'Search filters')}</span>
          <input value={query} onChange={event => setQuery(event.target.value)} placeholder={t('facets.policy.board.searchPlaceholder', 'Search filters by name or key')} />
          {query && <button type="button" onClick={() => setQuery('')} aria-label={t('facets.policy.board.clearSearch', 'Clear search')}>×</button>}
        </label>
        <div
          className="fp-segmented"
          aria-label={t('facets.policy.board.filterByType', 'Filter by type')}
          style={{ '--fp-segment-offset': `${typeFilterOptions.findIndex(([value]) => value === type) * 100}%` }}
        >
          <span className="fp-segmented-indicator" aria-hidden="true" />
          {typeFilterOptions.map(([value, label]) => (
            <button
              type="button"
              key={value}
              className={type === value ? 'is-active' : ''}
              aria-pressed={type === value}
              onClick={() => setType(value)}
            >{label}</button>
          ))}
        </div>
      </div>

      {(query || type !== 'all') && (
        <div className="fp-filter-note">
          <FilterListIcon />
          <span>{t('facets.policy.board.reorderingPaused', 'Reordering is paused while the list is filtered.')}</span>
          <button type="button" onClick={() => { setQuery(''); setType('all'); }}>{t('facets.policy.board.showFullOrder', 'Show full order')}</button>
        </div>
      )}

      <OrderedZoneBoard
        variant="continuous"
        zones={Object.keys(zoneCopy).map((zone) => {
          const copy = zoneCopy[zone];
          const ids = visible[zone];
          return {
            id: zone,
            ...copy,
            icon: zone === 'automatic' ? <SwapVertIcon /> : zone === 'first' ? <ExpandLessIcon /> : zone === 'hidden' ? <CloseIcon className="fp-hidden-zone-icon" /> : <ExpandMoreIcon />,
            tone: zone === 'automatic' || zone === 'hidden' ? 'automatic' : zone === 'last' ? 'danger' : 'positive',
            persistent: Boolean(copy.persistent),
            items: ids,
            countLabel: t('facets.policy.board.zoneCount', '{count} filters in {section}', { count: ids.length, section: copy.title }),
            emptyLabel: query || type !== 'all'
              ? t('facets.policy.board.noMatchesInSection', 'No matching filters in this section')
              : t('facets.policy.board.dropHere', 'Drop a filter here'),
          };
        })}
        disabled={Boolean(query || type !== 'all')}
        getItemId={id => id}
        onMove={onBoardMove}
        getItemProps={({ item: id }) => ({
          'data-testid': `facet-row-${id}`,
          'data-collection-row': true,
          onClick: () => onOpen(id),
        })}
        getItemClassName={({ zoneId, isDragging }) => `fp-facet-row fp-facet-row--${zoneId} ${query || type !== 'all' ? 'is-reorder-disabled' : ''} ${isDragging ? 'is-dragging' : ''}`}
        renderItem={({ item: id, zoneId, index }) => (
          <FacetRow
            facet={facets[id]}
            zone={zoneId}
            index={index}
            onOpen={onOpen}
            onMove={moveFacet}
            translate={t}
          />
        )}
      />
    </div>
  );
};

FacetOrderBoard.propTypes = {
  facets: PropTypes.objectOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
  })).isRequired,
  zones: PropTypes.shape({
    first: PropTypes.arrayOf(PropTypes.string),
    automatic: PropTypes.arrayOf(PropTypes.string),
    last: PropTypes.arrayOf(PropTypes.string),
    hidden: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onZonesChange: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  dirty: PropTypes.bool.isRequired,
  translate: PropTypes.func,
};

FacetOrderBoard.defaultProps = { translate: fallbackTranslate };

export default FacetOrderBoard;
